import React, { useState, useEffect } from "react";
import axios from "axios";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Telangana",
  "Karnataka",
  "Tamil Nadu",
  "Maharashtra",
  "Gujarat",
  "Delhi",
  "Uttar Pradesh",
  "West Bengal",
  "Kerala",
  "Rajasthan",
  "Madhya Pradesh",
  "Bihar",
  "Odisha",
  "Punjab",
  "Haryana",
  "Assam",
  "Jharkhand",
  "Chhattisgarh",
  "Uttarakhand",
  "Himachal Pradesh",
  "Goa",
  "Jammu & Kashmir",
  "Other",
];

// Helper to reliably extract clean numeric amount from string like "Rs. 8,600.00"
export const getNumericPrice = (priceStr) => {
  if (!priceStr) return 0;
  // Match digits with optional commas and decimals, ignoring any "Rs."
  const match = String(priceStr).match(/[\d,]+(\.\d+)?/);
  if (!match) return 0;
  const num = parseFloat(match[0].replace(/,/g, ""));
  return isNaN(num) ? 0 : Math.round(num);
};

// Google Apps Script Web App URL for storing donation entries in Google Sheet
export const GOOGLE_SHEET_URL =
  "https://script.google.com/macros/s/AKfycbzCliABwBTBSRHSlp94nVCQNuMMFbzw4VFLWSRejJKb1U9ysJJz5q2R_ByHBAmZ-c78Zw/exec";

// Ensure Razorpay SDK is loaded
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function BhagavatamDonationModal({
  isOpen,
  onClose,
  selectedLanguage,
  quantity,
  setQuantity,
  currentPrice,
  originalPrice,
  image,
}) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    address: "",
    city: "",
    state: "Andhra Pradesh",
    pincode: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    paymentId: "",
    orderId: "",
  });

  // Close on Escape key & Lock body scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose]);

  // Dynamically load Razorpay SDK on mount
  useEffect(() => {
    loadRazorpay();
  }, []);

  if (!isOpen) return null;

  const unitPrice = getNumericPrice(currentPrice);
  const totalAmount = unitPrice * (quantity || 1);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please enter your full name";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    const cleanedPhone = formData.mobile.replace(/\D/g, "");
    if (!cleanedPhone) {
      newErrors.mobile = "Please enter your 10-digit mobile number";
    } else if (cleanedPhone.length !== 10) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleIncrement = () => {
    if (setQuantity) setQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    if (setQuantity) setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // Helper to post all form and transaction data to Google Sheet
  const storeFormDataToGoogleSheet = async ({
    paymentStatus,
    paymentId,
    orderId,
  }) => {
    const payload = {
      timestamp: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
      }),
      paymentStatus: paymentStatus || "Successful",
      paymentId: paymentId || "N/A",
      orderId: orderId || "N/A",
      donorName: formData.fullName || "",
      email: formData.email || "",
      mobile: formData.mobile || "",
      address: formData.address || "N/A",
      city: formData.city || "N/A",
      state: formData.state || "N/A",
      pincode: formData.pincode || "N/A",
      bookTitle: "Srimad Bhagavatam (18/44 Volumes)",
      language: selectedLanguage,
      quantity: String(quantity),
      unitPrice: `₹${unitPrice.toLocaleString("en-IN")}`,
      totalAmount: `₹${totalAmount.toLocaleString("en-IN")}`,
    };

    try {
      const formBody = new URLSearchParams(payload).toString();
      await fetch(GOOGLE_SHEET_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody,
        mode: "no-cors",
      });
      console.log("Donation details recorded to Google Sheet successfully");
    } catch (sheetErr) {
      console.error("Error storing to Google Sheet:", sheetErr);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    // Make sure Razorpay script is ready
    await loadRazorpay();

    const rawApi =
      process.env.REACT_APP_API_URL || "https://www.iskconvijayawada.org/api";
    const API = rawApi.replace(/\/+$/, "");
    const razorpayKey =
      process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_cLFMe1tRrVmdlU";

    try {
      // 1. Create order on backend (trying with both URL formats if needed)
      let orderId = null;
      try {
        const orderRes = await axios.post(`${API}/api/payment/create-order`, {
          amount: totalAmount,
          currency: "INR",
        });
        if (orderRes?.data?.id) {
          orderId = orderRes.data.id;
        }
      } catch (orderErr) {
        // Fallback for direct /payment/create-order path
        try {
          const fallbackRes = await axios.post(`${API}/payment/create-order`, {
            amount: totalAmount,
            currency: "INR",
          });
          if (fallbackRes?.data?.id) {
            orderId = fallbackRes.data.id;
          }
        } catch (fbErr) {
          console.warn("Order creation notice:", fbErr.message);
        }
      }

      // 2. Open Razorpay Checkout Pop-up
      if (window.Razorpay) {
        const options = {
          key: razorpayKey,
          amount: totalAmount * 100, // in paise
          currency: "INR",
          name: "ISKCON Vijayawada",
          description: `Srimad Bhagavatam (${selectedLanguage}) x ${quantity} set(s)`,
          order_id: orderId || undefined,
          prefill: {
            name: formData.fullName,
            email: formData.email,
            contact: formData.mobile,
          },
          notes: {
            donationType: "Srimad Bhagavatam Set",
            language: selectedLanguage,
            quantity: String(quantity),
            unitPrice: String(unitPrice),
            totalAmount: String(totalAmount),
            address: formData.address,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
          },
          handler: async function (response) {
            try {
              // 1. Verify payment on backend
              await axios.post(`${API}/api/payment/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                donationDetails: {
                  ...formData,
                  amount: totalAmount,
                  selectedLanguage,
                  quantity,
                  donationType: "Srimad Bhagavatam Set",
                },
              });
            } catch (vErr) {
              console.warn("Backend verification notice:", vErr);
            }

            // 2. Store all form values & payment IDs to Google Sheet
            await storeFormDataToGoogleSheet({
              paymentStatus: "Successful",
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
            });

            setPaymentDetails({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
            });
            setPaymentSuccess(true);
            setSubmitting(false);
          },
          theme: { color: "#ea583a" },
          modal: {
            ondismiss: function () {
              setSubmitting(false);
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", async function (response) {
          alert(
            "Payment Failed: " +
              (response.error?.description || "Transaction was cancelled"),
          );
          // Also record failed attempt in Google Sheet for tracking
          await storeFormDataToGoogleSheet({
            paymentStatus: "Failed",
            paymentId: response.error?.metadata?.payment_id || "Failed",
            orderId: response.error?.metadata?.order_id || orderId || "N/A",
          });
          setSubmitting(false);
        });
        rzp.open();
      } else {
        // Fallback if Razorpay SDK failed to load
        await storeFormDataToGoogleSheet({
          paymentStatus: "Pending / Manual",
          paymentId: "OFFLINE-" + Date.now(),
          orderId: orderId || "N/A",
        });
        alert(
          `Hare Krishna, ${formData.fullName}! Your donation request for ${quantity} set(s) of Srimad Bhagavatam (${selectedLanguage}) for ₹${totalAmount.toLocaleString("en-IN")} has been submitted.`,
        );
        setPaymentSuccess(true);
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Donation initiation error:", err);
      alert(
        err.response?.data?.message ||
          "Failed to initiate payment. Please check your connection and try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-4xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 bg-[#fbf9f6] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#ea583a]/10 text-[#ea583a] flex items-center justify-center font-bold text-base">
              🕉️
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 tracking-tight font-garamond leading-none">
                Srimad Bhagavatam Donation
              </h3>
              <span className="text-[11px] text-gray-500 font-normal">
                ISKCON Vijayawada • Spiritual Book Distribution
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/70 transition-colors cursor-pointer select-none text-xl"
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        {/* Success View */}
        {paymentSuccess ? (
          <div className="p-8 sm:p-12 text-center flex flex-col items-center justify-center my-auto">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-3xl mb-4">
              ✓
            </div>
            <h2 className="text-2xl sm:text-3xl font-garamond font-normal text-gray-800 mb-2">
              Hare Krishna! Thank You for Your Offering
            </h2>
            <div className="text-gray-600 max-w-md mb-6 text-sm sm:text-base leading-relaxed">
              Your donation for{" "}
              <strong>
                {quantity} set(s) of Srimad Bhagavatam ({selectedLanguage})
              </strong>{" "}
              totalling{" "}
              <strong>₹{totalAmount.toLocaleString("en-IN")}.00</strong> has
              been successfully recorded. An official receipt has been sent to{" "}
              <strong>{formData.email}</strong>.
            </div>
            {paymentDetails.paymentId && (
              <div className="mb-5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-600 font-mono">
                Payment ID:{" "}
                <span className="font-semibold text-gray-800">
                  {paymentDetails.paymentId}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setPaymentSuccess(false);
                onClose();
              }}
              className="px-6 py-2.5 bg-[#ea583a] hover:bg-[#d8482b] text-white rounded-md text-sm font-medium transition-colors shadow-sm cursor-pointer"
            >
              Done & Return
            </button>
          </div>
        ) : (
          /* Main Two-Column Layout */
          <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* LEFT SIDE: Donor & Delivery Form */}
            <div className="md:col-span-7 p-5 sm:p-7 bg-white">
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 tracking-tight">
                  Donor & Delivery Information
                </h4>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  Enter your details for receipt and book dispatch.
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                {/* Full Name */}
                <div>
                  <label className="block text-[12px] font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 transition-colors ${
                      errors.fullName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-400 bg-red-50/20"
                        : "border-gray-300 focus:border-[#ea583a] focus:ring-[#ea583a]"
                    }`}
                  />
                  {errors.fullName && (
                    <span className="text-[11px] text-red-500 mt-0.5 block">
                      {errors.fullName}
                    </span>
                  )}
                </div>

                {/* Email & Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="name@example.com"
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 transition-colors ${
                        errors.email
                          ? "border-red-500 focus:border-red-500 focus:ring-red-400 bg-red-50/20"
                          : "border-gray-300 focus:border-[#ea583a] focus:ring-[#ea583a]"
                      }`}
                    />
                    {errors.email && (
                      <span className="text-[11px] text-red-500 mt-0.5 block">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2.5 text-xs text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md select-none">
                        +91
                      </span>
                      <input
                        type="tel"
                        name="mobile"
                        maxLength={10}
                        value={formData.mobile}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({ ...prev, mobile: val }));
                          if (errors.mobile) {
                            setErrors((prev) => ({ ...prev, mobile: "" }));
                          }
                        }}
                        placeholder="10-digit number"
                        className={`w-full px-3 py-2 text-sm border rounded-r-md focus:outline-none focus:ring-1 transition-colors ${
                          errors.mobile
                            ? "border-red-500 focus:border-red-500 focus:ring-red-400 bg-red-50/20"
                            : "border-gray-300 focus:border-[#ea583a] focus:ring-[#ea583a]"
                        }`}
                      />
                    </div>
                    {errors.mobile && (
                      <span className="text-[11px] text-red-500 mt-0.5 block">
                        {errors.mobile}
                      </span>
                    )}
                  </div>
                </div>

                {/* Delivery Address */}
                <div>
                  <label className="block text-[12px] font-medium text-gray-700 mb-1">
                    Delivery Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="House/Flat No., Street, Landmark"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[#ea583a] focus:ring-1 focus:ring-[#ea583a] transition-colors"
                  />
                </div>

                {/* City, State & Pincode */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="e.g. Vijayawada"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[#ea583a] focus:ring-1 focus:ring-[#ea583a] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#ea583a] focus:ring-1 focus:ring-[#ea583a] transition-colors"
                    >
                      {INDIAN_STATES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[12px] font-medium text-gray-700 mb-1">
                      PIN Code
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      maxLength={6}
                      value={formData.pincode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        setFormData((prev) => ({ ...prev, pincode: val }));
                      }}
                      placeholder="6 digits"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[#ea583a] focus:ring-1 focus:ring-[#ea583a] transition-colors"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 bg-[#ea583a] hover:bg-[#d8482b] disabled:bg-gray-400 text-white text-sm sm:text-base font-medium rounded-lg transition-all duration-150 flex items-center justify-center gap-2 shadow-md cursor-pointer select-none active:scale-[0.99]"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                        Initiating Payment...
                      </>
                    ) : (
                      <>
                        Proceed to Donate ₹{totalAmount.toLocaleString("en-IN")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* RIGHT SIDE: Selected Quantity, Price & Language Display */}
            <div className="md:col-span-5 p-5 sm:p-7 bg-[#faf8f5] flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-3.5">
                  Selected Item Summary
                </div>

                {/* Product Card with Thumbnail & Quantity Badge */}
                <div className="flex items-start gap-3.5 p-3 bg-white rounded-xl border border-amber-100/90 shadow-sm mb-4">
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                    <img
                      src={image}
                      alt="Srimad Bhagavatam"
                      className="w-full h-full object-contain p-1"
                    />
                    {/* Badge showing quantity */}
                    <span className="absolute -top-1.5 -right-1.5 bg-[#222222] text-white text-[11px] font-semibold w-5 h-5 rounded-full flex items-center justify-center shadow">
                      {quantity}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h5 className="font-garamond text-base font-medium text-gray-900 leading-snug line-clamp-2">
                      Srimad Bhagavatam (18/44 Volumes)
                    </h5>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-900">
                        Language: {selectedLanguage}
                      </span>
                    </div>
                    <div className="mt-1.5 text-xs text-gray-500">
                      Unit Price:{" "}
                      <span className="font-semibold text-gray-800">
                        {currentPrice}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Selected Quantity Adjuster */}
                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-amber-100/90 mb-3.5 text-xs sm:text-sm">
                  <span className="text-gray-700 font-medium">
                    Selected Quantity:
                  </span>
                  <div className="flex items-center gap-2 border border-gray-300 rounded bg-white px-1">
                    <button
                      type="button"
                      onClick={handleDecrement}
                      className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-black transition-colors cursor-pointer select-none font-bold"
                    >
                      −
                    </button>
                    <span className="w-6 text-center font-medium text-gray-800 text-xs">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-black transition-colors cursor-pointer select-none font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="space-y-1.5 py-3 border-t border-b border-amber-200/60 text-xs text-gray-600">
                  <div className="flex justify-between">
                    <span>Selected Language</span>
                    <span className="text-gray-900 font-medium">
                      {selectedLanguage}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Unit Price</span>
                    <span className="text-gray-900 font-medium">
                      {currentPrice}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Quantity</span>
                    <span className="text-gray-900 font-medium">
                      × {quantity}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="text-gray-900 font-medium">
                      ₹{totalAmount.toLocaleString("en-IN")}.00
                    </span>
                  </div>
                  <div className="flex justify-between text-green-700">
                    <span>Shipping & Packaging</span>
                    <span className="font-semibold uppercase">Free</span>
                  </div>
                </div>

                {/* Total */}
                <div className="pt-3 flex items-baseline justify-between">
                  <span className="text-xs sm:text-sm font-semibold text-gray-800">
                    Total Donation
                  </span>
                  <div className="text-right">
                    <span className="text-xl sm:text-2xl font-bold text-[#ea583a] tracking-tight">
                      ₹{totalAmount.toLocaleString("en-IN")}.00
                    </span>
                    <div className="text-[10px] text-gray-400">
                      All Taxes Included
                    </div>
                  </div>
                </div>
              </div>

              {/* Sacred quote note */}
              <div className="mt-4 pt-3 border-t border-amber-200/50">
                <div className="text-[11px] leading-relaxed text-amber-900/80 italic font-garamond bg-amber-50/60 p-2.5 rounded border-l-2 border-amber-400">
                  "One who gives the gift of Srimad-Bhagavatam on the day of the
                  full moon of Bhadra achieves the supreme goal of life."
                  <div className="not-italic font-sans text-[10px] text-amber-700 mt-1">
                    — Srimad Bhagavatam 12.13.13
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
